/**
 * Redsys Checkout — dirotta il checkout di Shopify verso il checkout ospitato.
 *
 * Shopify non consente gateway di pagamento personalizzati se non si e' partner
 * certificato per i pagamenti. La strada praticabile e' questa: il carrello
 * resta di Shopify, il pagamento avviene sul checkout ospitato con Redsys, e
 * l'ordine nasce su Shopify via Admin API solo dopo l'autorizzazione.
 *
 * Configurazione: window.RedsysCheckout = { url, locale, enabled, intercept, quickBuy }.
 * La imposta il blocco dell'app (o lo snippet del tema, se si e' scelta quella
 * strada).
 */
(function () {
  'use strict';

  var config = window.RedsysCheckout || {};
  if (!config.enabled || !config.url) return;
  // "intercept" assente = acceso: chi usa lo snippet non deve impostare nulla.
  if (config.intercept === false) return;

  var base = String(config.url).replace(/\/+$/, '');
  var busy = false;

  /** base64url di una stringa UTF-8. */
  function encodePayload(payload) {
    var json = JSON.stringify(payload);
    var bytes = new TextEncoder().encode(json);
    var binary = '';
    for (var i = 0; i < bytes.length; i += 1) binary += String.fromCharCode(bytes[i]);
    return btoa(binary).replace(/\+/g, '-').replace(/\//g, '_').replace(/=+$/, '');
  }

  /** Un cookie per nome, senza librerie. */
  function leggiCookie(nome) {
    var parti = ('; ' + document.cookie).split('; ' + nome + '=');
    if (parti.length !== 2) return '';
    return parti.pop().split(';').shift() || '';
  }

  /**
   * Identificativo dell'evento, condiviso col checkout ospitato.
   *
   * Serve alla deduplica di Meta: lo stesso `InitiateCheckout` puo' arrivare
   * da qui e, piu' tardi, dal checkout. Con lo stesso `eventID` ne conta uno.
   */
  function nuovoEventId() {
    if (window.crypto && window.crypto.randomUUID) return 'ic_' + window.crypto.randomUUID();
    return 'ic_' + Date.now().toString(36) + Math.random().toString(36).slice(2, 10);
  }

  /**
   * `InitiateCheckout` sparato QUI, sul dominio del negozio, prima di andarsene.
   *
   * Perche' non basta mandarlo dal checkout ospitato:
   *  - il checkout sta su un altro dominio e ha un `_fbp` suo, quindi per Meta
   *    e' un'altra persona: il passaggio non si attacca alla visita;
   *  - chi non arriva mai al checkout (rete lenta, ripensamento, blocco) non
   *    genererebbe alcun evento, e il funnel perde proprio il gradino su cui
   *    si ottimizzano le campagne;
   *  - il pixel di Shopify non emette `checkout_started`, perche' il checkout
   *    di Shopify non viene mai aperto: lo intercettiamo prima.
   *
   * Non solleva mai: un pixel assente o bloccato non deve impedire di pagare.
   */
  function tracciaInizioCheckout(cart, eventId) {
    try {
      var fbq = window.fbq;
      var ttq = window.ttq;
      var haMeta = typeof fbq === 'function';
      var haTikTok = ttq && typeof ttq.track === 'function';
      if (!haMeta && !haTikTok) return false;

      var items = cart.items || [];
      var contents = items.map(function (item) {
        return {
          id: String(item.product_id || item.variant_id),
          quantity: item.quantity,
          item_price: (item.final_price || item.price || 0) / 100,
        };
      });

      var valuta =
        cart.currency || (window.Shopify && window.Shopify.currency && window.Shopify.currency.active);
      // Shopify tiene gli importi in centesimi, i pixel li vogliono in unita'.
      var valore = (cart.total_price || 0) / 100;
      var pezzi = items.reduce(function (somma, item) { return somma + (item.quantity || 0); }, 0);

      if (haMeta) {
        fbq(
          'track',
          'InitiateCheckout',
          {
            currency: valuta,
            value: valore,
            num_items: pezzi,
            content_type: 'product',
            content_ids: contents.map(function (voce) { return voce.id; }),
            contents: contents,
          },
          { eventID: eventId },
        );
      }

      if (haTikTok) {
        // TikTok nomina i campi a modo suo: `content_id` e `price` invece di
        // `id` e `item_price`, e la deduplica sta in `event_id`.
        ttq.track(
          'InitiateCheckout',
          {
            currency: valuta,
            value: valore,
            contents: contents.map(function (voce) {
              return {
                content_id: voce.id,
                content_type: 'product',
                quantity: voce.quantity,
                price: voce.item_price,
              };
            }),
          },
          { event_id: eventId },
        );
      }

      return true;
    } catch (errore) {
      console.warn('[Redsys] InitiateCheckout non inviato:', errore);
      return false;
    }
  }

  /**
   * Lascia al pixel il tempo di partire prima della navigazione.
   *
   * Il pixel invia con `sendBeacon` dove c'e', e quello sopravvive al cambio
   * di pagina; dove non c'e' usa un'immagine, e una navigazione immediata la
   * tronca. Una pausa breve copre il secondo caso senza farsi notare: il
   * pulsante e' gia' in stato "sto lavorando".
   */
  function respiro(inviato) {
    if (!inviato) return Promise.resolve();
    var attesa = navigator.sendBeacon ? 80 : 300;
    return new Promise(function (risolvi) { setTimeout(risolvi, attesa); });
  }

  /** Legge il carrello reale da Shopify: mai fidarsi del DOM. */
  function readCart() {
    return fetch(window.Shopify && window.Shopify.routes && window.Shopify.routes.root
      ? window.Shopify.routes.root + 'cart.js'
      : '/cart.js', { headers: { Accept: 'application/json' }, credentials: 'same-origin' })
      .then(function (response) {
        if (!response.ok) throw new Error('cart.js ' + response.status);
        return response.json();
      });
  }

  function buildPayload(cart, eventId) {
    var items = (cart.items || []).map(function (item) {
      var properties = null;
      if (item.properties && Object.keys(item.properties).length) {
        properties = {};
        Object.keys(item.properties).forEach(function (key) {
          // Le proprieta' che iniziano con _ sono interne al tema: non servono.
          if (key.charAt(0) !== '_') properties[key] = String(item.properties[key]);
        });
        if (!Object.keys(properties).length) properties = null;
      }
      return {
        id: String(item.variant_id),
        quantity: item.quantity,
        properties: properties,
      };
    });

    var locale = config.locale
      || (window.Shopify && window.Shopify.locale)
      || document.documentElement.lang
      || undefined;

    var payload = {
      items: items,
      locale: locale ? String(locale).slice(0, 2) : undefined,
      returnUrl: window.location.origin + (window.Shopify && window.Shopify.routes ? window.Shopify.routes.root : '/'),
    };

    if (cart.note) payload.note = String(cart.note).slice(0, 500);

    // Il checkout ospitato riusa questo id per il proprio InitiateCheckout,
    // cosi' Meta riconosce il doppione e conta una volta sola.
    if (eventId) payload.icEventId = eventId;

    // I cookie di Meta vivono sul dominio del negozio; il checkout sta altrove
    // e non puo' leggerli. Senza, l'acquisto non risale alla campagna che l'ha
    // portato: il cliente che paga sembra arrivato dal nulla.
    var fbp = leggiCookie('_fbp');
    var fbc = leggiCookie('_fbc');
    if (!fbc) {
      // Primo clic dall'inserzione: il cookie non c'e' ancora, ma l'identificativo
      // e' nell'indirizzo. Il formato lo impone Meta: fb.1.<tempo>.<fbclid>.
      var fbclid = new URLSearchParams(window.location.search).get('fbclid');
      if (fbclid) fbc = 'fb.1.' + Date.now() + '.' + fbclid;
    }
    if (fbp) payload.fbp = fbp;
    if (fbc) payload.fbc = fbc;

    // Lo stesso per TikTok: il cookie `_ttp` identifica il visitatore, e al
    // primo clic dall'inserzione il click id sta solo nell'indirizzo.
    var ttp = leggiCookie('_ttp');
    var ttclid = leggiCookie('ttclid') || new URLSearchParams(window.location.search).get('ttclid');
    if (ttp) payload.ttp = ttp;
    if (ttclid) payload.ttclid = ttclid;

    // Codice sconto gia' applicato sul carrello (Shopify lo espone come discount code).
    var discount = (cart.cart_level_discount_applications || [])[0];
    if (discount && discount.title) payload.discountCode = discount.title;

    return payload;
  }

  function goToCheckout(event) {
    if (busy) return;

    var cartUrl = base + '/checkout';
    if (event) {
      event.preventDefault();
      event.stopPropagation();
    }
    busy = true;
    markBusy(true);

    readCart()
      .then(function (cart) {
        if (!cart.items || !cart.items.length) {
          busy = false;
          markBusy(false);
          window.location.href = '/cart';
          return;
        }

        // L'evento parte da qui, dal dominio del negozio, con i dati veri del
        // carrello. Solo dopo si lascia il sito.
        var eventId = nuovoEventId();
        var inviato = tracciaInizioCheckout(cart, eventId);
        var destinazione = cartUrl + '?cart=' + encodePayload(buildPayload(cart, eventId));

        return respiro(inviato).then(function () {
          window.location.href = destinazione;
        });
      })
      .catch(function (error) {
        console.error('[Redsys] impossibile leggere il carrello:', error);
        busy = false;
        markBusy(false);
        // Meglio il checkout di Shopify che un pulsante morto.
        window.location.href = '/checkout';
      });
  }

  /** Segnala che stiamo lavorando, cosi' nessuno clicca due volte. */
  function markBusy(state) {
    document.querySelectorAll('[data-redsys-checkout], [name="checkout"]').forEach(function (element) {
      if (state) {
        element.setAttribute('aria-busy', 'true');
        element.classList.add('redsys-busy');
        if ('disabled' in element) element.disabled = true;
      } else {
        element.removeAttribute('aria-busy');
        element.classList.remove('redsys-busy');
        if ('disabled' in element) element.disabled = false;
      }
    });
  }

  var CHECKOUT_SELECTORS = [
    '[name="checkout"]',
    '.shopify-payment-button__button',
    '[data-redsys-checkout]',
    'a[href$="/checkout"]',
    'a[href*="/checkout?"]',
    '.cart__checkout-button',
    '#checkout',
  ].join(',');

  // Delega sull'intero documento: funziona anche con i carrelli a tendina
  // che vengono ridisegnati dopo ogni aggiunta.
  document.addEventListener(
    'click',
    function (event) {
      var target = event.target instanceof Element ? event.target.closest(CHECKOUT_SELECTORS) : null;
      if (!target) return;
      // I pulsanti di pagamento rapido di Shopify hanno un flusso tutto loro:
      // li tocchiamo solo se il negoziante lo ha chiesto esplicitamente.
      if (!config.quickBuy && target.closest('.shopify-payment-button')) return;
      goToCheckout(event);
    },
    true
  );

  // Alcuni temi inviano il form del carrello senza passare da un click.
  document.addEventListener(
    'submit',
    function (event) {
      var form = event.target;
      if (!(form instanceof HTMLFormElement)) return;
      if (!/\/cart/.test(form.getAttribute('action') || '')) return;
      var submitter = event.submitter;
      if (submitter && submitter.getAttribute('name') !== 'checkout') return;
      goToCheckout(event);
    },
    true
  );

  window.RedsysCheckout.go = goToCheckout;
})();
